// this helper was added to help avoid data collision between different acids
export const BEVERAGE_SESSION_ACID_KEY = "beverageSessionAcid";

export const BEVERAGE_USER_SESSION_KEYS = [
  "beverageConsentResponded",
  "beverageConsentOptedOut",
  "pendingBeverageSelections",
  "pendingBeverageV3Selections",
  "pendingBeverageV5Selections",
  "pendingBeverageV6Selections",
  "pendingBeverageV7Selections",
  "savedBeveragePreferences",
  "savedBeverageV3Preferences",
  "savedBeverageV5Preferences",
  "beverageSessionId",
  "beverageV3SessionId",
  "beverageV5SessionId",
  "beverages.storage",
  "beveragesV2.storage",
  "beveragesV2.currentStorage",
  "beveragesV2.beverageV2Description",
  "messages.beveragesV4",
];

/**
 * Pending-selections blobs are written moments before an SSO redirect, by
 * the same guest whose identity the redirect is about to confirm — they
 * aren't leftover data from a different guest the way everything else in
 * BEVERAGE_USER_SESSION_KEYS can be. Without this exemption, the acid-change
 * guard below can (and does) delete a redirect's own in-flight data before
 * the destination page ever gets a chance to restore it, since acid
 * resolution and this clear both happen before that restore runs. A blob
 * only qualifies while recently written; anything older is treated as
 * genuine stale/cross-guest data and cleared as before.
 */
const RECENT_REDIRECT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

const PENDING_REDIRECT_KEY_GROUPS = [
  { pendingKey: "pendingBeverageSelections", sessionIdKey: "beverageSessionId" },
  { pendingKey: "pendingBeverageV3Selections", sessionIdKey: "beverageV3SessionId" },
  { pendingKey: "pendingBeverageV5Selections", sessionIdKey: "beverageV5SessionId" },
  { pendingKey: "pendingBeverageV6Selections", sessionIdKey: null },
  { pendingKey: "pendingBeverageV7Selections", sessionIdKey: null },
];

const isFreshPendingRedirect = (storage, pendingKey) => {
  try {
    const raw = storage.getItem(pendingKey);
    if (!raw) {
      return false;
    }
    const timestamp = JSON.parse(raw)?.timestamp;
    return (
      typeof timestamp === "number" &&
      Date.now() - timestamp <= RECENT_REDIRECT_WINDOW_MS
    );
  } catch {
    return false;
  }
};

export const clearBeverageUserSession = (storage = sessionStorage) => {
  const preserve = new Set();
  PENDING_REDIRECT_KEY_GROUPS.forEach(({ pendingKey, sessionIdKey }) => {
    if (isFreshPendingRedirect(storage, pendingKey)) {
      preserve.add(pendingKey);
      if (sessionIdKey) {
        preserve.add(sessionIdKey);
      }
    }
  });

  BEVERAGE_USER_SESSION_KEYS.forEach((key) => {
    if (!preserve.has(key)) {
      storage.removeItem(key);
    }
  });
};

export const getStoredBeverageConsent = (storage = sessionStorage) => {
  const rawOptedOut = storage.getItem("beverageConsentOptedOut");
  const optedOut = rawOptedOut !== null ? rawOptedOut === "true" : null;

  return {
    /**
     * Requires optedOut to actually be present, not just the responded
     * marker — a legacy/partial session that only has
     * beverageConsentResponded would otherwise be trusted as answered while
     * never having told CDP which way the guest actually opted, letting
     * both the prompt and the CDP write be skipped indefinitely.
     */
    hasResponded:
      storage.getItem("beverageConsentResponded") === "true" &&
      optedOut !== null,
    optedOut,
  };
};

export const shouldResetBeverageStateForAcidChange = (
  previousAcid,
  nextAcid,
) => Boolean(previousAcid && previousAcid !== nextAcid);

export const syncBeverageUserSessionForAcid = (
  acid,
  storage = sessionStorage,
  previousStoredAcid = storage.getItem("acid"),
) => {
  const previousAcid =
    storage.getItem(BEVERAGE_SESSION_ACID_KEY) || previousStoredAcid;

  if (acid) {
    if (previousAcid && previousAcid !== acid) {
      clearBeverageUserSession(storage);
    }
    storage.setItem(BEVERAGE_SESSION_ACID_KEY, acid);
    return;
  }

  if (previousAcid) {
    clearBeverageUserSession(storage);
    storage.removeItem(BEVERAGE_SESSION_ACID_KEY);
  }
};
